import useSkipModules from '~/composables/useSkipModules';
import { useMobileAppsDetector } from '~/composables/useMobileAppsDetector';
import { type PublicationContext } from '~/types';

/*
 * Client half of the OneTrust integration: consent listeners, the native
 * app bridge and the Preference Center deep link. Readiness is recovered
 * from `OnetrustActiveGroups`, so plugin order doesn't matter.
 */
export default defineNuxtPlugin((nuxt) => {
  const { skipModule, moduleNames } = useSkipModules(nuxt._route);
  if (skipModule(moduleNames.ONETRUST)) { return; }

  const rtConfPub = useRuntimeConfig().public;
  if (!rtConfPub?.oneTrustCookie?.domain) { return; }

  let isPreferenceCenterOpenPending = false;
  let isCookieSettingsHashHandled = false;
  let hasReceivedInitialConsentSignal = false;
  let isTcfListenerRegistered = false;
  let lastSyncedTcString = '';

  const mobileApps = useMobileAppsDetector();
  const router = useRouter();

  function checkMobileAppAndSendCookieSyncEvent () {
    if (mobileApps.isAndroid() || mobileApps.isIos()) {
      const messageBody = {
        type: 'sync_tc_string',
        message: 'Sync TC String'
      };

      if (mobileApps.isIos() && window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.syncTCString) {
        window.webkit.messageHandlers.syncTCString.postMessage(messageBody);
      } else {
        const interfaceToUse = rtConfPub.name?.toLowerCase() === 'dn' ? window.DNInterface : window.GlobalInterface;
        if (interfaceToUse) {
          interfaceToUse.syncTCString(JSON.stringify(messageBody));
        }
      }
    }
  }

  /*
   * Before a consent decision the banner owns the screen — drop the deep
   * link. `null` = SDK not ready; `OptanonWrapper` retries once it can.
   */
  function resolveCookieSettingsDeepLink () {
    const hasConsentDecision = hasClosedConsentBanner();

    if (hasConsentDecision === null) {
      isPreferenceCenterOpenPending = true;
      return;
    }

    isPreferenceCenterOpenPending = false;

    if (!hasConsentDecision) {
      clearCookieSettingsHash();
      return;
    }

    openPreferenceCenter();
  }

  /* `replace` keeps the arrival history entry, so Back still works. */
  function clearCookieSettingsHash () {
    const currentRoute = router.currentRoute.value;
    if (!isCookieSettingsHash(currentRoute.hash)) {
      return Promise.resolve();
    }
    return router.replace({ path: currentRoute.path, query: currentRoute.query, hash: '' });
  }

  /*
   * Honoured: the hash stays while the dialog is open, cleared on close.
   * Dropped: cleared straight off. The flag resets on navigation away.
   */
  function handleCookieSettingsHash (hash: string) {
    if (!isCookieSettingsHash(hash)) {
      isCookieSettingsHashHandled = false;
      /* Dialog lives outside the app root — close it on navigation away. */
      if (isPreferenceCenterOpen()) { closePreferenceCenter(); }
      return;
    }
    if (isCookieSettingsHashHandled) {
      return;
    }
    isCookieSettingsHashHandled = true;
    resolveCookieSettingsDeepLink();
  }

  /*
   * Only `useractioncomplete` guarantees a final TC string for the native
   * apps. Registered from the readiness signal because `__tcfapi` comes
   * from the CMP stub; absent entirely means TCF is out of scope here.
   */
  function registerTcfListener () {
    if (isTcfListenerRegistered || typeof window.__tcfapi !== 'function') {
      return;
    }
    isTcfListenerRegistered = true;

    window.__tcfapi('addEventListener', 2, (tcData, success) => {
      if (!success || !tcData?.tcString) {
        return;
      }

      /*
       * `tcloaded` seeds the baseline; `cmpuishown` is skipped — its string
       * describes the dialog, not a decision.
       */
      if (tcData.eventStatus === 'tcloaded') {
        lastSyncedTcString = tcData.tcString;
        return;
      }

      if (tcData.eventStatus !== 'useractioncomplete' || tcData.tcString === lastSyncedTcString) {
        return;
      }
      lastSyncedTcString = tcData.tcString;
      checkMobileAppAndSendCookieSyncEvent();
    });
  }

  /*
   * Fires on script load and every consent update — one listener covers
   * readiness and changes. Native sync lives in `registerTcfListener`.
   */
  window.addEventListener('OneTrustGroupsUpdated', () => {
    if (!hasReceivedInitialConsentSignal) {
      hasReceivedInitialConsentSignal = true;
      registerTcfListener();
      return;
    }

    /* No-ops unless the current hash is the cookie settings one. */
    clearCookieSettingsHash();
  });

  /*
   * An Escape dismissal fires no OneTrust event, so the keypress itself is
   * the close signal. The open check spares every other Escape on the page.
   */
  window.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !isPreferenceCenterOpen()) { return; }
    clearCookieSettingsHash();
  }, true);

  /*
   * The SSR'd script can finish before the client bundle runs, missing the
   * load fire above; `OnetrustActiveGroups` set means readiness was reached.
   */
  if (typeof window.OnetrustActiveGroups === 'string') {
    hasReceivedInitialConsentSignal = true;
    registerTcfListener();
  }

  /*
   * The SDK invokes `OptanonWrapper` once initialised, whatever the consent
   * state — `OneTrustGroupsUpdated` may wait for a banner choice on a first
   * visit. The slot is a single function: anything else writing to it wins.
   */
  window.OptanonWrapper = () => {
    if (isPreferenceCenterOpenPending) { resolveCookieSettingsDeepLink(); }
  };

  router.isReady().then(() => {
    handleCookieSettingsHash(router.currentRoute.value.hash);
  });

  router.afterEach((to) => {
    handleCookieSettingsHash(to.hash);
  });
});
